import { StepShell } from '../components/layout';
import { MultiChoiceCards, TagSelect, SelectionCounter } from '../components/selection';

/**
 * Four sections — strengths, interests, industries and how others see you —
 * are the same interaction with different copy and option sets. Rather than
 * four near-identical components, the registry builds them from a spec.
 */
export function makeSelectionStep(spec) {
  function SelectionStep({ data, update, notes = {}, config }) {
    const values = data[spec.field] || [];
    const options = config[spec.optionsKey];
    const Control = spec.variant === 'tags' ? TagSelect : MultiChoiceCards;

    return (
      <StepShell
        eyebrow={spec.eyebrow}
        title={spec.title}
        lede={spec.lede}
        why={spec.why}
        encouragement={spec.encouragement}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="ob-group-label" style={{ marginBottom: 0 }}>{spec.prompt}</span>
          <SelectionCounter count={values.length} min={spec.min} max={spec.max} />
        </div>

        <Control
          options={options}
          values={values}
          max={spec.max}
          columns={spec.columns || 2}
          note={notes[spec.field]}
          onChange={(next) => update({ [spec.field]: next })}
        />
      </StepShell>
    );
  }

  SelectionStep.displayName = `SelectionStep(${spec.field})`;
  return SelectionStep;
}

export const STRENGTHS_SPEC = {
  eyebrow: 'Step 9 of 18',
  title: 'What comes naturally to you',
  lede: 'The things you do without really thinking about it. Not what you are best at on paper — what you find easy.',
  prompt: 'Choose 3 to 5',
  why: 'People do their best work when they are doing more of what already comes easily. This helps us shape your first project around that.',
  encouragement: 'If you are stuck, think about what friends ask you for help with.',
  field: 'strengths',
  optionsKey: 'strengths',
  min: 3,
  max: 5,
  variant: 'cards',
  columns: 2,
};

export const INTERESTS_SPEC = {
  eyebrow: 'Step 10 of 18',
  title: 'What genuinely interests you',
  lede: 'Not what looks good on an application — the things you would read about on a slow afternoon.',
  prompt: 'Choose at least 3',
  why: 'Curiosity is the best predictor of who sticks with something. We would rather place you somewhere you are interested than somewhere you merely qualify for.',
  field: 'interests',
  optionsKey: 'interests',
  min: 3,
  max: 8,
  variant: 'tags',
};

export const INDUSTRIES_SPEC = {
  eyebrow: 'Step 12 of 18',
  title: 'Industries that appeal to you',
  lede: 'Where would you like the work to sit? You are not committing to anything — this is just a sense of direction.',
  prompt: 'Choose up to 3',
  why: 'Our client projects span a lot of sectors. Knowing what draws you helps us put you on work you will care about.',
  field: 'industries',
  optionsKey: 'industries',
  min: 1,
  max: 3,
  variant: 'tags',
};

export const PERCEPTION_SPEC = {
  eyebrow: 'Step 14 of 18',
  title: 'How others describe you',
  lede: 'Think about what people who know you well actually say — not how you would describe yourself on a good day.',
  prompt: 'Choose 3 to 6',
  why: 'How you come across to a team is often different from how you see yourself, and both are worth knowing before you join one.',
  encouragement: 'There are no better or worse answers here. Every team needs all of these.',
  field: 'traits',
  optionsKey: 'perceptionTraits',
  min: 3,
  max: 6,
  variant: 'cards',
  columns: 2,
};
